import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Section} from 'constants/NavConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs, type AccessibleGraphQLObject} from 'graphql-client/InaccessibleInfo';
import {withoutUnresolvedAaSequenceNodes} from 'graphql-client/WithoutUnresolvedAaSequenceNode';
import navigateToURL from 'lib/navigateToURL';
import type {ProteinAminoAcidsQuery} from 'model-hub/__generated/ProteinAminoAcidsQuery';
import InputChip from 'model-hub/lib/InputChip';
import {PREDICTION_JOB_MOLECULE_SMILES_QUERY, PROTEIN_AMINO_ACIDS_QUERY} from 'model-hub/ModelHubQueries';
import {getComponentLetter} from 'model-hub/new-prediction/newPredictionState';
import {
  isAaSequenceIdInputComponent,
  isAminoAcidsInputComponent,
  isMoleculeIdInputComponent,
  isProteinIdInputComponent,
  isSmilesInputComponent,
  resolvePredictionInputComponents,
  type PredictionInputComponent,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import {navigateToProteinStructureTab} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import {expandSection} from 'nav/sidepanel/SidepanelActions';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import APIUtil from 'util/APIUtil';
import type {BasicEvent} from 'util/Types';

import type {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type PredictionJobMoleculeNode = AccessibleGraphQLObject & {
  __typename: 'MoleculeNode';
  currentVersion: {
    chemicalStructure: {
      canonicalSmiles: string;
    };
  };
};

interface PredictionJobMoleculeSmilesQuery {
  molecules: Array<PredictionJobMoleculeNode | InaccessibleInfoGraphQL>;
}

export type PreviewSequenceContentKind = 'amino_acids' | 'smiles';

/**
 * A single row of a prediction's input table: either a named or positionally labeled raw
 * sequence/SMILES, or a registry entity whose sequence was resolved via GraphQL.
 */
export interface PreviewSequenceDisplayItem {
  key: string;
  entityId: string | null;
  /** Component name or positional Chain/Ligand fallback for raw inputs; null for registry entities. */
  rawLabel: string | null;
  contentKind: PreviewSequenceContentKind;
  text: string;
}

function getCouldNotLoadContentMessage(contentKind: PreviewSequenceContentKind): string {
  if (contentKind === 'smiles') {
    if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      return 'Molecule chemistry is not enabled on this tenant.';
    }
    return 'Could not load molecule SMILES.';
  }
  return 'Could not load protein sequence.';
}

function previewItemFromComponent(
  component: PredictionInputComponent,
  index: number,
  positionalLabel: string,
  proteinAminoAcidsById: ReadonlyMap<string, string>,
  moleculeSmilesById: ReadonlyMap<string, string>
): PreviewSequenceDisplayItem {
  if (isAminoAcidsInputComponent(component)) {
    return {
      key: `${index}-aminoAcids`,
      entityId: null,
      rawLabel: component.name ?? positionalLabel,
      contentKind: 'amino_acids',
      text: component.value,
    };
  }
  if (isSmilesInputComponent(component)) {
    return {
      key: `${index}-smiles`,
      entityId: null,
      rawLabel: component.name ?? positionalLabel,
      contentKind: 'smiles',
      text: component.value,
    };
  }
  if (isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component)) {
    const entityId = component.id;
    const aminoAcids = proteinAminoAcidsById.get(entityId);
    return {
      key: `${index}-${component._type}`,
      entityId,
      rawLabel: null,
      contentKind: 'amino_acids',
      text: aminoAcids ?? getCouldNotLoadContentMessage('amino_acids'),
    };
  }
  if (isMoleculeIdInputComponent(component)) {
    const smiles = moleculeSmilesById.get(component.id);
    return {
      key: `${index}-molecule`,
      entityId: component.id,
      rawLabel: null,
      contentKind: 'smiles',
      text: smiles ?? getCouldNotLoadContentMessage('smiles'),
    };
  }
  return {
    key: `${index}-unknown`,
    entityId: null,
    rawLabel: 'Component',
    contentKind: 'amino_acids',
    text: '—',
  };
}

/**
 * Resolves a prediction's inputs into display rows: raw components render their inline sequence/SMILES,
 * while registry-entity components (proteins, molecules) are hydrated via GraphQL. Entity IDs are
 * de-duplicated before fetching.
 */
export async function resolvePreviewSequenceItems(
  componentApiIds: ReadonlyArray<string>,
  model: ScientificModelNode,
  configJson?: Record<string, unknown> | null
): Promise<Array<PreviewSequenceDisplayItem>> {
  const components = resolvePredictionInputComponents(componentApiIds, configJson, model);

  const proteinIds = Array.from(
    new Set(
      components.flatMap((component) =>
        isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component) ? [component.id] : []
      )
    )
  );
  const moleculeIds = Array.from(
    new Set(components.flatMap((component) => (isMoleculeIdInputComponent(component) ? [component.id] : [])))
  );

  const proteinAminoAcidsById = new Map<string, string>();
  if (proteinIds.length > 0) {
    const proteinResult = await doQuerySafe<ProteinAminoAcidsQuery>(
      {query: PROTEIN_AMINO_ACIDS_QUERY},
      {variables: {ids: proteinIds}}
    );
    for (const protein of accessibleObjs(withoutUnresolvedAaSequenceNodes(proteinResult.proteins))) {
      proteinAminoAcidsById.set(protein.id, protein.experimentalCurrentVersion?.aminoAcids ?? '');
    }
  }

  const moleculeSmilesById = new Map<string, string>();
  if (moleculeIds.length > 0 && BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    const moleculeResult = await doQuerySafe<PredictionJobMoleculeSmilesQuery>(
      {query: PREDICTION_JOB_MOLECULE_SMILES_QUERY},
      {variables: {moleculeIds}}
    );
    for (const molecule of accessibleObjs<PredictionJobMoleculeNode>(moleculeResult.molecules)) {
      moleculeSmilesById.set(molecule.id, molecule.currentVersion.chemicalStructure.canonicalSmiles);
    }
  }

  let aminoAcidCount = 0;
  let ligandCount = 0;

  return components.map((component, index) => {
    const isLigand = isSmilesInputComponent(component) || isMoleculeIdInputComponent(component);
    let positionalLabel: string;
    if (isLigand) {
      ligandCount += 1;
      positionalLabel = `Ligand ${getComponentLetter(ligandCount)}`;
    } else {
      aminoAcidCount += 1;
      positionalLabel = `Chain ${getComponentLetter(aminoAcidCount)}`;
    }
    return previewItemFromComponent(
      component,
      index,
      positionalLabel,
      proteinAminoAcidsById,
      moleculeSmilesById
    );
  });
}

function handlePreviewChipClick(
  event: {stopPropagation: () => void},
  apiId: string,
  item: {editUrl?: string}
): void {
  event.stopPropagation();
  if (APIUtil.isProtein(apiId)) {
    navigateToProteinStructureTab(apiId).catch(() => {});
    return;
  }
  expandSection(Section.PROJECTS);
  if (item.editUrl != null) {
    navigateToURL(item.editUrl);
  }
}

/** The left "Component" cell of an input row: a clickable entity chip, or a raw input chip. */
export function PreviewSequenceNameCell({item}: {item: PreviewSequenceDisplayItem}): JSX.Element {
  if (item.entityId != null) {
    return (
      <BenchChip
        id={item.entityId}
        size={ChipSize.SMALL}
        onClick={(event: BasicEvent, chipItem: {editUrl?: string}) => {
          handlePreviewChipClick(event, item.entityId!, chipItem);
        }}
      />
    );
  }
  return (
    <InputChip
      label={item.rawLabel ?? 'Component'}
      type={item.contentKind === 'smiles' ? 'smiles' : 'aminoAcids'}
      value={item.text}
    />
  );
}
